import { Controller } from "@hotwired/stimulus";
import Tribute from "tributejs";

export default class extends Controller {
    static get values() {
        return {
            searchMentionablesUrl: String,
            values: Array,
            searchTagsUrl: String
        };
    }

    static get targets() {
        return ["message", "messageHtml", "mentionedTeamIds", "mentionedUserIds", "valueIds", "tags", "messageEditor", "toolbarButton", "emojiPicker"];
    }

    async connect() {
        this.toolbarButtonTargets.forEach(tbt => new bootstrap.Tooltip(tbt));
        const { data, Picker } = await import("imports/emoji-mart");
        const picker = new Picker({
            data,
            onEmojiSelect: (e) => {
                this.insertEmoji(e.native);
            }
        });
        this.emojiPickerTarget.appendChild(picker);
    }

    async messageEditorTargetConnected(target) {
        const tributeCollection = [];

        if (this.hasSearchMentionablesUrlValue) {
            tributeCollection.push({
                trigger: "@",
                values: async (text, callback) => {
                    const urlSearchParams = new URLSearchParams();
                    urlSearchParams.append("searchText", text);
                    urlSearchParams.append("page", 1)
                    urlSearchParams.append("pageSize", 10);
                    const response = await fetch(`${this.searchMentionablesUrlValue}?${urlSearchParams}`);
                    callback(await response.json());
                },
                lookup: "text",
                menuItemTemplate: function (item) {
                    return `<img src="${item.original.imageUrl}" alt="">${item.original.text}`;
                },
                selectTemplate: function (item) {
                    return `<span class="badge" contenteditable="false" data-mentioned-${item.original.type}-id="${item.original.id}" data-value="@(${item.original.type[0]}:${item.original.id}:${item.original.text})">
                            @${item.original.text}
                        </span>`;
                },
                searchOpts: {
                    skip: true
                }
            });
        }

        if (this.hasValuesValue && this.valuesValue.length > 0) {
            tributeCollection.push({
                trigger: "*",
                values: this.valuesValue.map(v => ({ value: v.Value, text: v.Text })),
                lookup: "text",
                menuItemLimit: 10,
                menuItemTemplate: function (item) {
                    return item.original.text;
                },
                selectTemplate: function (item) {
                    return `<span class="badge" contenteditable="false" data-value-id="${item.original.value}" data-value="*(${item.original.value}:${item.original.text})">
                                ☆${item.original.text}
                            </span>`;
                }
            });
        }

        if (this.hasSearchTagsUrlValue) {
            tributeCollection.push({
                trigger: "#",
                values: async (text, callback) => {
                    const urlSearchParams = new URLSearchParams();
                    urlSearchParams.append("searchText", text);
                    const response = await fetch(`${this.searchTagsUrlValue}?${urlSearchParams}`);
                    callback(await response.json());
                },
                lookup: "text",
                menuItemTemplate: function (item) {
                    return item.original.text;
                },
                selectTemplate: function (item) {
                    return `<span class="badge" contenteditable="false" data-tag="${item.original.text}" data-value="#(${item.original.text})">
                                #${item.original.text}
                            </span>`;
                },
                searchOpts: {
                    skip: true
                }
            });
        }

        if (tributeCollection.length > 0) {
            this.messageEditorTarget.tribute = new Tribute({
                collection: tributeCollection,
                containerClass: "c-feed-post-dropdown-menu",
                itemClass: "c-feed-post-dropdown-menu__item",
                allowSpaces: true
            });
            this.messageEditorTarget.tribute.attach(target);
        }
    }

    preventMultilineEntry(e) {
        if (e.keyCode === 13) {
            e.preventDefault();
        }
    }

    togglePlaceholder(e) {
        e.target.classList.toggle("has-content", e.target.textContent);
    }

    updateMessageValue() {
        let message = this.messageEditorTarget.cloneNode(true);
        message.querySelectorAll(".badge")?.forEach(b => b.replaceWith(b.dataset.value));
        this.messageTarget.value = message.textContent;
        this.messageHtmlTarget.value = this.messageEditorTarget.innerHTML;

        if (this.hasMentionedTeamIdsTarget) {
            this.mentionedTeamIdsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-mentioned-team-id]")].map(b => b.dataset.mentionedTeamId))].join(",");
        }

        if (this.hasMentionedUserIdsTarget) {
            this.mentionedUserIdsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-mentioned-user-id]")].map(b => b.dataset.mentionedUserId))].join(",");
        }

        if (this.hasValueIdsTarget) {
            this.valueIdsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-value-id]")].map(b => b.dataset.valueId))].join(",");
        }

        if (this.hasTagsTarget) {
            this.tagsTarget.value = [...new Set([...this.messageEditorTarget.querySelectorAll("[data-tag]")].map(b => b.dataset.tag))].join(",");
        }
    }

    pasteText(e) {
        e.preventDefault();
        const text = e.clipboardData.getData("text");
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        selection.deleteFromDocument();
        selection.getRangeAt(0).insertNode(document.createTextNode(text));
        selection.collapseToEnd();
        this.updateMessageValue();
        this.messageEditorTarget.classList.add("has-content");
    }

    preventDragOver(e) {
        e.preventDefault();
    }

    preventDrop(e) {
        e.preventDefault();
    }

    mentionSomeone(e) {
        e.preventDefault();
        this.showMenuForCollection("@");
    }

    addAValue(e) {
        e.preventDefault();
        this.showMenuForCollection("*");
    }

    addATag(e) {
        e.preventDefault();
        this.showMenuForCollection("#");
    }

    saveMessageEditorSelectionRange() {
        const selection = getSelection();

        if (this.messageEditorTarget.contains(selection.anchorNode)) {
            this.detailsEditorSelectionRange = selection.getRangeAt(0);
        } else {
            this.detailsEditorSelectionRange = document.createRange();
            this.detailsEditorSelectionRange.selectNodeContents(this.messageEditorTarget);
            this.detailsEditorSelectionRange.collapse();
        }
    }

    insertEmoji(emoji) {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(this.detailsEditorSelectionRange);
        this.detailsEditorSelectionRange.deleteContents();
        this.detailsEditorSelectionRange.insertNode(document.createTextNode(emoji + "\u00A0"));
        this.detailsEditorSelectionRange.collapse();
        this.updateMessageValue();
        this.messageEditorTarget.classList.add("has-content");
        bootstrap.Dropdown.getOrCreateInstance(this.emojiPickerTarget.closest(".dropdown-center").querySelector("[data-bs-toggle='dropdown']")).hide();
    }

    showMenuForCollection(trigger) {
        if (!this.messageEditorTarget.classList.contains("has-content")) {
            this.messageEditorTarget.innerHTML = "<span></span>";
        }

        this.messageEditorTarget.tribute.showMenuForCollection(this.messageEditorTarget, this.messageEditorTarget.tribute.collection.findIndex(c => c.trigger === trigger));
        this.messageEditorTarget.classList.add("has-content");
    }
}